import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API, { apis } from "common/apis";
import {
  IVendorB2cApi,
  IVendorSkuMapping,
  ICreateVendorSkuMappingsPayload,
} from "common/interface/IVendorItems";
import { logoutIfNeeded } from "common/utils";

interface VendorItemsState {
  vendors?: IVendorB2cApi;
  mappings: IVendorSkuMapping[];
  status: "loading" | "saving" | "deleting" | "error" | "idle";
  error?: string;
}

const initialState: VendorItemsState = {
  mappings: [],
  status: "idle",
};

export const getVendorB2cAll = createAsyncThunk<
  IVendorB2cApi,
  { pageNo?: number; pageSize?: number }
>("vendorItems/getVendorB2cAll", async ({ pageNo = 1, pageSize = 25 }, thunkApi) => {
  try {
    const response = await API.get(apis.vendorB2cAll, {
      params: { pageNo, pageSize },
    });
    const body = response.data;
    if (Array.isArray(body)) {
      return {
        data: body,
        pageNo: 1,
        pageSize: body.length,
        total: body.length,
        pages: 1,
      } as IVendorB2cApi;
    }
    return body as IVendorB2cApi;
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || "Something went wrong!";
    logoutIfNeeded(error, thunkApi);
    return thunkApi.rejectWithValue({ message: errorMessage });
  }
});

export const getVendorSkuMappings = createAsyncThunk<IVendorSkuMapping[], number>(
  "vendorItems/getVendorSkuMappings",
  async (vendorId, thunkApi) => {
    try {
      const response = await API.get(apis.vendorSkuMapping, {
        params: { vendorId },
      });
      const body = response.data;
      if (Array.isArray(body)) {
        return body as IVendorSkuMapping[];
      }
      if (body && Array.isArray(body.data)) {
        return body.data as IVendorSkuMapping[];
      }
      return [] as IVendorSkuMapping[];
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const createVendorSkuMapping = createAsyncThunk<void, ICreateVendorSkuMappingsPayload>(
  "vendorItems/createVendorSkuMapping",
  async (payload, thunkApi) => {
    try {
      const response = await API.post(apis.vendorSkuMapping, payload);
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const updateVendorSkuMappingReliability = createAsyncThunk<
  void,
  { vendorId: number; purchaseCode: string; reliabilityScore: number }
>(
  "vendorItems/updateVendorSkuMappingReliability",
  async ({ vendorId, purchaseCode, reliabilityScore }, thunkApi) => {
    try {
      const response = await API.patch(
        `${apis.vendorSkuMapping}/${vendorId}/${purchaseCode}`,
        { reliabilityScore }
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

export const deleteVendorSkuMapping = createAsyncThunk<
  void,
  { vendorId: number; purchaseCode: string }
>(
  "vendorItems/deleteVendorSkuMapping",
  async ({ vendorId, purchaseCode }, thunkApi) => {
    try {
      const response = await API.delete(
        `${apis.vendorSkuMapping}/${vendorId}/${purchaseCode}`
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Something went wrong!";
      logoutIfNeeded(error, thunkApi);
      return thunkApi.rejectWithValue({ message: errorMessage });
    }
  }
);

const vendorItemsSlice = createSlice({
  name: "vendorItems",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getVendorB2cAll.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(getVendorB2cAll.fulfilled, (state, action) => {
        state.status = "idle";
        state.error = "";
        state.vendors = action.payload;
      })
      .addCase(getVendorB2cAll.rejected, (state, action) => {
        state.status = "error";
        const payload = action.payload;
        if (payload !== null && typeof payload === "object" && "message" in payload) {
          state.error = String(payload.message);
        }
      })
      .addCase(getVendorSkuMappings.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(getVendorSkuMappings.fulfilled, (state, action) => {
        state.status = "idle";
        state.error = "";
        state.mappings = action.payload;
      })
      .addCase(getVendorSkuMappings.rejected, (state, action) => {
        state.status = "error";
        const payload = action.payload;
        if (payload !== null && typeof payload === "object" && "message" in payload) {
          state.error = String(payload.message);
        }
      })
      .addCase(createVendorSkuMapping.pending, (state) => {
        state.status = "saving";
        state.error = "";
      })
      .addCase(createVendorSkuMapping.fulfilled, (state) => {
        state.status = "idle";
        state.error = "";
      })
      .addCase(createVendorSkuMapping.rejected, (state, action) => {
        state.status = "error";
        const payload = action.payload;
        if (payload !== null && typeof payload === "object" && "message" in payload) {
          state.error = String(payload.message);
        }
      })
      .addCase(updateVendorSkuMappingReliability.pending, (state) => {
        state.status = "saving";
        state.error = "";
      })
      .addCase(updateVendorSkuMappingReliability.fulfilled, (state) => {
        state.status = "idle";
        state.error = "";
      })
      .addCase(updateVendorSkuMappingReliability.rejected, (state, action) => {
        state.status = "error";
        const payload = action.payload;
        if (payload !== null && typeof payload === "object" && "message" in payload) {
          state.error = String(payload.message);
        }
      })
      .addCase(deleteVendorSkuMapping.pending, (state) => {
        state.status = "deleting";
        state.error = "";
      })
      .addCase(deleteVendorSkuMapping.fulfilled, (state) => {
        state.status = "idle";
        state.error = "";
      })
      .addCase(deleteVendorSkuMapping.rejected, (state, action) => {
        state.status = "error";
        const payload = action.payload;
        if (payload !== null && typeof payload === "object" && "message" in payload) {
          state.error = String(payload.message);
        }
      });
  },
});

export default vendorItemsSlice.reducer;
